export const DEFAULT_ORG_TIMEZONE = "Europe/London";

export type TimezoneOption = { key: string; value: string; text: string };

const formatTimezoneName = (timeZone: string): string =>
  timeZone.replace(/\//g, " / ").replace(/_/g, " ");

export const formatTimezoneOffset = (
  timeZone: string,
  date = new Date(),
): string => {
  try {
    const formatter = new Intl.DateTimeFormat("en-GB", {
      timeZone,
      timeZoneName: "shortOffset",
    });
    const offset = formatter
      .formatToParts(date)
      .find((part) => part.type === "timeZoneName")?.value;

    if (!offset || offset === "GMT" || offset === "UTC") {
      return "+00:00";
    }

    const normalized = offset.replace(/^GMT/, "UTC");
    const match = normalized.match(/UTC([+-])(\d{1,2})(?::(\d{2}))?/);
    if (!match) {
      return "+00:00";
    }

    const [, sign, hourPart, minutePart] = match;
    if (!sign || !hourPart) {
      return "+00:00";
    }
    const hours = hourPart.padStart(2, "0");
    const minutes = (minutePart ?? "00").padStart(2, "0");
    return `${sign}${hours}:${minutes}`;
  } catch {
    return "+00:00";
  }
};

const getOffsetMinutes = (timeZone: string, date = new Date()): number => {
  const offset = formatTimezoneOffset(timeZone, date);
  const match = offset.match(/([+-])(\d{2}):(\d{2})/);
  if (!match) return 0;

  const [, signPart, hourPart, minutePart] = match;
  if (!signPart || !hourPart || !minutePart) return 0;
  const sign = signPart === "+" ? 1 : -1;
  return sign * (parseInt(hourPart, 10) * 60 + parseInt(minutePart, 10));
};

export const formatTimezoneLabel = (
  timeZone: string,
  date = new Date(),
): string =>
  `${formatTimezoneName(timeZone)} (${formatTimezoneOffset(timeZone, date)})`;

export const getTimezoneOptions = (date = new Date()): TimezoneOption[] =>
  Intl.supportedValuesOf("timeZone")
    .map((timezone) => ({
      key: timezone,
      value: timezone,
      text: formatTimezoneLabel(timezone, date),
      offsetMinutes: getOffsetMinutes(timezone, date),
    }))
    .sort(
      (left, right) =>
        left.offsetMinutes - right.offsetMinutes ||
        left.text.localeCompare(right.text),
    )
    .map(({ key, value, text }) => ({ key, value, text }));
